<template>
	<div class="w-full">
		<GridLayout>
			<template #left-header>
				<Title value="FILTROS" />
			</template>
			<template #left-content>
				<div class="w-full h-full">
					<Filters
						ref="filters"
						:filters="filters"
						@check-all="CheckAll"
						@change="ChangeFiltersHandler"
					/>
				</div>
			</template>
			<template #left-footer>
				<div class="w-8/12 h-full flex justify-center items-center gap-3">
					<button
						class="btn btn-fixed-size"
						@click="ClearFilters"
					>
						LIMPAR
					</button>
					<button
						class="btn btn-fixed-size"
						@click="ApplyConfigs"
					>
						APLICAR
					</button>
				</div>
			</template>
			<template #right-header>
				<Title value="MENSAGERIA" />
			</template>
			<template #right-tool-bar>
				<ToolBar class="w-full flex justify-between">
					<div class="h-full flex items-center">
						<button
							class="btn btn-tool"
							:class="{
								'has-filters': hasAtLeastOneFilter,
							}"
							@click="FiltersHandler"
						>
							<i
								class="fa-filter mx-2 font-20"
								:class="hasAtLeastOneFilter ? 'fas' : 'far'"
							></i>
						</button>
					</div>
					<div class="h-full flex items-center gap-5">
						<div class="flex items-center cursor-pointer messages-switch">
							<label
								class="mb-0 font-weight-bold cursor-pointer"
								@click="ToggleMessages"
							>
								INATIVO
							</label>
							<div
								class="px-4 font-40"
								:class="{
									'text-primary': active,
									'text-secondary': !active,
								}"
								@click="ToggleMessages"
							>
								<i
									class="fad"
									:class="{
										'fa-toggle-on': active,
										'fa-toggle-off': !active,
									}"
								></i>
							</div>
							<label
								class="mb-0 font-weight-bold cursor-pointer"
								@click="ToggleMessages"
							>
								ATIVO
							</label>
						</div>
						<div class="flex items-center">
							<button
								class="btn btn-tool"
								@click="NewConfig"
							>
								<i class="fal fa-plus mx-2 font-32"></i>
							</button>
						</div>
					</div>
				</ToolBar>
			</template>
			<template #right-content>
				<MainContent class="px-4">
					<div class="w-full mt-3 select-all-container">
						<div class="form-check flex items-center gap-4">
							<input
								id="select-all-configs"
								type="checkbox"
								class=""
								name="select-all-configs"
								:checked="selectAllConfigs"
								hidden
							/>
							<label
								class="form-check-label"
								for="select-all-configs"
								@click.stop.prevent="CheckAllConfigs"
							>
							</label>
							<span
								class="text-2xl font-bold"
								@click="CheckAllConfigs"
								>TODOS
							</span>
						</div>
					</div>
					<div
						v-if="configs.length"
						class="w-full h-full flex flex-col items-center overflow-y-auto"
					>
						<div class="w-full my-3 configs-container">
							<div
								v-for="(config, index) in orderedConfigs"
								:key="`${config.id}-${index}`"
								class="w-full flex items-center config-wrapper gap-4"
							>
								<div class="form-check">
									<input
										:id="`config-${config.id}-${index}`"
										type="checkbox"
										class=""
										:name="`config-${config.id}-${index}`"
										:checked="config.selected"
										hidden
									/>
									<label
										class="form-check-label"
										:for="`config-${config.id}-${index}`"
										@click.stop.prevent="SelectConfig(config)"
									>
									</label>
								</div>
								<div
									class="w-full px-4 config-container"
									@click.stop="EditConfig(config)"
								>
									<div class="w-full flex py-3 pr-5 items-center">
										<div class="flex items-center">
											<div
												class="mx-2 col-auto font-40"
												:class="{
													'text-primary': config.status,
													'text-secondary': !config.status,
												}"
											>
												<i
													class="fad cursor-pointer"
													:class="{
														'fa-toggle-on': config.status,
														'fa-toggle-off': !config.status,
													}"
													@click.stop="ToggleConfig([config.id])"
												></i>
											</div>
										</div>
										<div class="flex-1 data">
											<p class="mb-0 text-3xl font-semibold">
												{{ GetParsedInterval(config) }}
											</p>
											<span class="mb-0 text-muted">
												{{ GetDefaultMessage(config) }}
											</span>
										</div>
									</div>
								</div>
								<div class="flex items-center actions">
									<button
										class="btn btn-unshaped btn-outline-danger font-24"
										@click.stop="DeleteConfigAlert([config.id])"
									>
										<i class="fal fa-trash"></i>
									</button>
								</div>
							</div>
						</div>
					</div>
				</MainContent>
			</template>
			<template #right-footer>
				<div class="w-full h-full flex justify-between items-center gap-3">
					<button
						class="btn btn-danger btn-fixed-size"
						:disabled="!selectedConfigs.length"
						@click="DeleteConfigAlert(selectedConfigs)"
					>
						DELETAR
					</button>
					<button
						class="btn btn-success btn-fixed-size"
						@click="ApplyConfigs"
					>
						SALVAR
					</button>
				</div>
			</template>
		</GridLayout>
		<MissingDataModal
			v-if="showMissingModal"
			:organization="organization"
			@close="showMissingModal = false"
			@save="SaveOrganizationPhone"
		/>
		<ConfigModal
			v-if="showConfigModal"
			:config="selectedConfig"
			@close="showConfigModal = false"
			@save="SaveConfig"
		/>
	</div>
</template>

<script>
	import _ from 'lodash'
	import Swal from 'sweetalert2'
	import { mapGetters, mapActions } from 'vuex'
	import { useEachRecursively, useGetSelectedsRecursively } from '@/composables'
	import { useConfirmation, useDeleteAlert } from '@/composables'

	import Filters from './components/Filters.vue'
	import MissingDataModal from './components/MissingDataModal.vue'
	import ConfigModal from './components/ConfigModal.vue'

	export default {
		name: 'Messages',
		components: {
			Filters,
			MissingDataModal,
			ConfigModal,
		},
		data() {
			return {
				organization: {},
				active: false,
				configs: [],
				step: 'filters',
				filters: {
					select_all: false,
					modules: [],
					showSidebar: false,
				},
				savedFilters: {
					select_all: false,
					modules: [],
					showSidebar: false,
				},
				appliedFilters: null,
				showMissingModal: false,
				showConfigModal: false,
				selectedConfig: {},
				selectAllConfigs: false,
				changeFiltersTimeout: null,
			}
		},
		computed: {
			...mapGetters('auth', ['requester_selected_id']),
			...mapGetters('system', ['showSidebar']),
			hasAtLeastOneFilter() {
				let hasFilter = false

				useEachRecursively(this.filters.modules, (item, skipRef) => {
					if (item.selected) {
						hasFilter = true
						skipRef.skip = true
						return
					}
				})

				return hasFilter
			},
			orderedConfigs() {
				return _.orderBy(this.configs, ['interval_type_order', 'interval_number'], ['asc', 'asc'])
			},
			selectedConfigs() {
				return this.configs.filter(c => c.selected).map(c => c.id)
			},
		},
		watch: {
			showSidebar: {
				handler(val) {
					this.filters.showSidebar = val ?? false
					this.ChangeFiltersHandler()
				},
			},
		},
		methods: {
			...mapActions('auth', ['GetAuthAccount']),
			...mapActions('system', ['ToggleShowSidebar']),
			FiltersHandler() {
				this.ToggleShowSidebar()
			},
			GetMessagesConfigs() {
				return window.api
					.call('get', `/messages/messages-configs/${this.requester_selected_id}`)
					.then(({ status, data }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro ao obter os dados da Organização!',
							})

							return
						}

						this.organization = data.organization
						this.active = this.organization.instance.config.messages
					})
			},
			ToggleMessages() {
				if (!this.active && !this.IsValidOrganization()) {
					this.showMissingModal = true

					return
				}

				return window.api
					.call('get', `/messages/toggle-messages/${this.organization.id}`)
					.then(({ status }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro ao habilitar/desabilitar a Mensageria!',
							})

							return
						}

						this.active = !this.active

						this.GetAuthAccount()
					})
			},
			IsValidOrganization() {
				const haveName = this.organization.name
				const havePhone = this.organization.phone_1 || this.organization.phone_2

				if (!haveName || !havePhone) return false

				return true
			},
			SaveOrganizationPhone(newPhone) {
				return window.api
					.call('patch', `/messages/save-phone/${this.organization.id}`, {
						phone: newPhone,
					})
					.then(({ status }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro ao salvar o número de telefone!',
							})

							return
						}

						useConfirmation()

						this.showMissingModal = false
						this.active = true

						this.DefaultMessagesConfigsHandler()
					})
			},
			DefaultMessagesConfigsHandler() {
				if (!this.active) return

				this.$refs.filters.CheckAll(true)

				this.ApplyConfigs()
			},
			GetFiltersHierarchy() {
				return window.api
					.call('get', `/messages/filters-hierarchy/${this.organization.id}`)
					.then(async ({ status, data }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro ao obter os filtros para configuração!',
							})

							return
						}

						this.filters.modules = await this.StructFiltersHierarchyRecursively([...data.filters_hierarchy])
					})
			},
			async StructFiltersHierarchyRecursively(hierarchy) {
				hierarchy = await Promise.all(
					hierarchy.map(async item => {
						const newItem = {
							id: item.id,
							name: item.name,
							selected: false,
							expanded: false,
							...this.GetCurrentStructure(item),
						}

						if (newItem.children) {
							newItem.children = await this.StructFiltersHierarchyRecursively(newItem.children)

							delete newItem[newItem.oldChildrenKey]
							delete newItem.oldChildrenKey
						} else {
							delete newItem.children
						}

						return newItem
					})
				)

				return hierarchy
			},
			GetCurrentStructure(item) {
				const current = {}

				if (item.modalities && Array.isArray(item.modalities)) {
					current.key = 'modules'
					current.children = item.modalities
					current.identifier = 'module'
					current.oldChildrenKey = 'modalities'
				} else if (item.schedules && Array.isArray(item.schedules)) {
					current.key = 'modalities'
					current.children = item.schedules
					current.identifier = 'modality'
					current.oldChildrenKey = 'schedules'
				} else {
					current.key = 'schedules'
					current.children = null
					current.identifier = 'schedule'
				}

				return current
			},
			// CONFIGS
			GetConfigs() {
				return window.api
					.call('post', '/messages/get-configs', {
						organization_id: this.organization.id,
					})
					.then(({ status, data }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro obter as mensagens configuradas!',
							})

							return
						}

						this.configs = [...data.configs].map(c => {
							return {
								...c,
								selected: false,
							}
						})
					})
			},
			GetParsedInterval(config) {
				let parsed = ''

				let intervalTypeDescription = 'hora'

				switch (config.interval_type) {
					case 'day':
						intervalTypeDescription = 'dia'
						break
					case 'week':
						intervalTypeDescription = 'semana'
						break
					case 'month':
						intervalTypeDescription = 'mês'
						break
					case 'year':
						intervalTypeDescription = 'ano'
						break
				}

				if (config.interval_number > 1) {
					intervalTypeDescription = `${intervalTypeDescription}s`

					if (config.interval_type === 'month') {
						intervalTypeDescription = 'meses'
					}
				}

				parsed = `${config.interval_number} ${intervalTypeDescription} antes:`

				return parsed
			},
			GetDefaultMessage(config) {
				return `Olá, [nome do(a) paciente], aqui é [nome da instituição]...`
			},
			NewConfig() {
				this.selectedConfig = {}

				this.showConfigModal = true
			},
			SaveConfig(config) {
				return window.api
					.call('post', '/messages/save-config', {
						organization_id: this.organization.id,
						interval_number: config.interval_number,
						interval_type: config.interval_type,
						id: config.id || null,
					})
					.then(({ status }) => {
						if (status !== 201) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro ao salvar a configuração da mensagem!',
							})

							return
						}

						this.GetConfigs()

						useConfirmation()
					})
			},
			CheckAllConfigs() {
				this.selectAllConfigs = !this.selectAllConfigs

				this.configs.forEach(c => this.SelectConfig(c, this.selectAllConfigs))
			},
			SelectConfig(config, state = null) {
				config.selected = state ?? !config.selected
			},
			EditConfig(config) {
				this.selectedConfig = config

				this.showConfigModal = true
			},
			ToggleConfig(configs_ids, state = null) {
				return window.api
					.call('post', '/messages/toggle-configs', {
						configs_ids,
					})
					.then(({ status }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro ao habilitar/desabilitar a(s) mensagem(ns)!',
							})

							return
						}

						this.configs = this.configs.map(c => {
							if (configs_ids.includes(c.id)) {
								c.status = state !== null ? state : !c.status
							}

							return c
						})
					})
			},
			async DeleteConfigAlert(configs_ids) {
				const { isConfirmed } = await useDeleteAlert()

				if (isConfirmed) {
					this.DeleteConfigs(configs_ids)
				}
			},
			DeleteConfigs(configs_ids) {
				return window.api
					.call('post', '/messages/delete-configs/', {
						configs_ids,
					})
					.then(({ status }) => {
						if (status !== 204) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro ao excluir a(s) configuração(ões) de mensagem!',
							})

							return
						}

						this.configs = this.configs.filter(c => !configs_ids.includes(c.id))

						useConfirmation()
					})
			},
			ApplyConfigs() {
				const { schedules } = useGetSelectedsRecursively(this.filters.modules)

				return window.api
					.call('post', '/messages/apply-configs', {
						organization_id: this.organization.id,
						schedules_ids: schedules,
					})
					.then(({ status }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro ao aplicar as configurações!',
							})

							return
						}

						useConfirmation()
					})
			},
			// FILTERS
			CheckAll(e) {
				this.filters.select_all = e
				this.ChangeFiltersHandler()
			},
			ClearFilters() {
				this.$refs.filters.ClearFilters()
			},
			ChangeFiltersHandler() {
				console.log('ChangeFiltersHandler')
				if (this.changeFiltersTimeout) clearTimeout(this.changeFiltersTimeout)

				this.changeFiltersTimeout = setTimeout(() => {
					this.SaveFilters()
				}, 1000)
			},
			GetFilters() {
				return window.api
					.call('post', '/messages/get-filters', {
						organization_id: this.organization.id,
					})
					.then(async ({ status, data }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro ao salvar os filtros!',
							})

							return
						}

						const isNewFilters = !data.filters

						this.savedFilters = !isNewFilters ? { ...data.filters } : { ...this.filters }

						await this.SelectByFilters()
						await this.ToggleShowSidebar(this.savedFilters.showSidebar)

						if (isNewFilters) this.SaveFilters()
					})
			},
			SelectByFilters() {
				this.filters.select_all = this.savedFilters.select_all

				this.savedFilters.modules.forEach(module => {
					const filtersModule = this.filters.modules.find(m => m.id === module.id)
					filtersModule.expanded = module.expanded

					if (!module.selected) return
					if (!filtersModule) return
					filtersModule.selected = true

					module.children.forEach(modality => {
						const filtersModality = filtersModule.children.find(m => m.id === modality.id)
						filtersModality.expanded = modality.expanded

						if (!modality.selected) return
						if (!filtersModality) return
						filtersModality.selected = true

						modality.children.forEach(schedule => {
							if (!schedule.selected) return

							const filtersSchedule = filtersModality.children.find(s => s.id === schedule.id)
							if (!filtersSchedule) return
							filtersSchedule.selected = true
						})
					})
				})
			},
			SaveFilters() {
				return window.api
					.call('post', '/messages/save-filters', {
						organization_id: this.organization.id,
						filters: this.filters,
					})
					.then(({ status }) => {
						if (status !== 200) {
							Swal.fire({
								icon: 'error',
								title: 'Erro!',
								text: 'Erro ao salvar os filtros!',
							})

							return
						}
					})
			},
		},
		async beforeMount() {
			await this.GetMessagesConfigs()
			await this.GetFiltersHierarchy()
			await this.GetConfigs()
			await this.GetFilters()
		},
	}
</script>

<style lang="scss" scoped>
	.messages-switch {
		padding: 0 0.5rem;
		background-color: var(--backgroundLight);
		border: 1px solid var(--backgroundLight);
		border-radius: 0.5rem;

		color: var(--fontColor);

		.font-40 {
			line-height: 1;
		}
	}

	.configs-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;

		.config-wrapper {
			.config-container {
				background-color: var(--backgroundLight);
				color: var(--fontColor);
				border-radius: 1rem;
				cursor: pointer;

				&:hover {
					background-color: var(--newPrimaryDark);
					color: #fff;

					.text-muted {
						color: #fff !important;
					}
				}
			}
		}
	}
</style>
